import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/Button';
import { useToast } from '../components/Toast';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import { ProductModal } from '../components/ProductModal';
import { CategoryModal } from '../components/CategoryModal';
import { OptionModal } from '../components/OptionModal';
import { ProductVisualModal } from '../components/ProductVisualModal';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { productsService, categoriesService, optionsService, choiceLibraryService, uploadService, syncAllLibraryToOptions } from '../services/firebaseService';
import { Plus, Edit2, Trash2, Package, Copy, FolderOpen, Sliders, Edit, MoreVertical, Clock, Calendar, XCircle, CheckCircle, ArrowUp, ArrowDown, LayoutGrid, List as ListIcon, Search, X, Filter, Grid, Image, GripVertical, Menu, RefreshCw, Download } from 'lucide-react';
import { ProductsListView } from '../components/ProductsListView';
import { CategoriesListView } from '../components/CategoriesListView';
import { OptionsListView } from '../components/OptionsListView';
import { ChoiceLibraryModal } from '../components/ChoiceLibraryModal';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// LEGACY: Utilisé pour la rétrocompatibilité avec les anciennes images (Python backend)
// Les nouvelles images viennent de Firebase Storage (https://firebasestorage.googleapis.com/...)
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Composant pour chaque produit draggable
const SortableProductItem = ({ product, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  let imageUrl = product.image_url || product.image;
  if (imageUrl && !imageUrl.startsWith('http')) {
    imageUrl = `${API_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-3 bg-white border rounded-lg mb-2 ${isDragging ? 'shadow-lg border-purple-400' : 'border-gray-200'}`}
    >
      {/* Handle de drag */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-2 hover:bg-gray-100 rounded"
      >
        <Menu className="w-5 h-5 text-gray-400" />
      </div>

      {/* Position */}
      <span className="w-8 h-8 flex items-center justify-center bg-purple-100 text-purple-700 rounded-full text-sm font-bold">
        {index + 1}
      </span>

      {/* Image */}
      {imageUrl ? (
        <img src={imageUrl} alt={product.name} className="w-12 h-12 object-cover rounded-lg" />
      ) : (
        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
          <Package className="w-6 h-6 text-gray-400" />
        </div>
      )}

      {/* Nom */}
      <span className="flex-1 font-medium text-gray-900 truncate">{product.name}</span>

      {/* Prix */}
      <span className="text-gray-600 font-bold">{(product.base_price || 0).toFixed(2)}€</span>
    </div>
  );
};

export const MenuManagement = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('products');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [optionsViewMode, setOptionsViewMode] = useState('grid'); // 'grid' ou 'list' pour options
  const [optionsSearchTerm, setOptionsSearchTerm] = useState('');
  const [syncing, setSyncing] = useState(false); // Recherche options
  const [syncingFromLibrary, setSyncingFromLibrary] = useState(false); // Sync Biblio → Options
  const [categoriesSearchTerm, setCategoriesSearchTerm] = useState(''); // Recherche catégories
  const [draggedCategoryIndex, setDraggedCategoryIndex] = useState(null);
  const [draggedProductIndex, setDraggedProductIndex] = useState(null);
  
  // Choice Library state
  const [choiceLibrary, setChoiceLibrary] = useState([]);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [editingChoice, setEditingChoice] = useState(null);
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const [libraryViewMode, setLibraryViewMode] = useState('grid'); // 'grid' ou 'list'
  const [showUnusedChoices, setShowUnusedChoices] = useState(false); // Choix non utilisés
  
  // Products state
  const [products, setProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showVisualModal, setShowVisualModal] = useState(false);
  const [editingVisualProduct, setEditingVisualProduct] = useState(null);
  const [openStockMenu, setOpenStockMenu] = useState(null);
  const [showOutOfStockOnly, setShowOutOfStockOnly] = useState(false);
  
  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPromo, setFilterPromo] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const [filterBadge, setFilterBadge] = useState('all');
  const [showOrphanProducts, setShowOrphanProducts] = useState(false); // Produits sans catégorie
  const [expandedCategory, setExpandedCategory] = useState(null);
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Options state
  const [options, setOptions] = useState([]);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [showUnusedOptions, setShowUnusedOptions] = useState(false); // Filtre options non utilisées

  // Sort modal state
  const [showSortModal, setShowSortModal] = useState(false);
  const [sortingCategory, setSortingCategory] = useState(null);
  const [sortedProducts, setSortedProducts] = useState([]);
  const [savingSort, setSavingSort] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  // Close stock menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openStockMenu && !e.target.closest('.relative')) {
        setOpenStockMenu(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openStockMenu]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadProducts(),
        loadCategories(),
        loadOptions(),
        loadChoiceLibrary()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      // FIREBASE DIRECT
      const result = await productsService.getAll();
      setProducts(result.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Erreur', 'Impossible de charger les produits');
    }
  };

  const loadCategories = async () => {
    try {
      // FIREBASE DIRECT
      const result = await categoriesService.getAll();
      setCategories(result.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Erreur', 'Impossible de charger les catégories');
    }
  };

  const loadOptions = async () => {
    try {
      // FIREBASE DIRECT
      const result = await optionsService.getAll();
      setOptions(result.options || []);
    } catch (error) {
      console.error('Error loading options:', error);
      toast.error('Erreur', 'Impossible de charger les options');
    }
  };

  const loadChoiceLibrary = async () => {
    try {
      // FIREBASE DIRECT
      const result = await choiceLibraryService.getAll();
      setChoiceLibrary(result.choices || []);
    } catch (error) {
      console.error('Error loading choice library:', error);
      toast.error('Erreur', 'Impossible de charger la bibliothèque de choix');
    }
  };

  // Vérifier si un choix de la bibliothèque est utilisé dans au moins une option
  const isChoiceUsed = (choiceName) => {
    const lowerName = choiceName.toLowerCase().trim();
    return options.some(opt =>
      opt.choices?.some(c => c.name?.toLowerCase().trim() === lowerName)
    );
  };

  // Vérifier si une option est utilisée dans au moins un produit
  const isOptionUsed = (optionId) => {
    return products.some(p => p.option_ids?.includes(optionId));
  };

  // === FILTRAGE DES PRODUITS ===
  const filteredProducts = products.filter(product => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchName = product.name?.toLowerCase().includes(query);
      const matchDesc = product.description?.toLowerCase().includes(query);
      if (!matchName && !matchDesc) return false;
    }
    if (filterPromo && !product.isPromo) return false;
    if (filterCategory !== 'all' && product.category !== filterCategory) return false;
    if (filterStock === 'available' && product.is_out_of_stock) return false;
    if (filterStock === 'out_of_stock' && !product.is_out_of_stock) return false;
    if (showOutOfStockOnly && !product.is_out_of_stock) return false;
    // Filtre produits sans catégorie (orphelins)
    if (showOrphanProducts && (product.category_id || product.category)) return false;
    // Filtre par badge
    if (filterBadge !== 'all') {
      if (filterBadge === 'none') {
        // Filtrer les produits sans badge
        if (product.badge && product.badge !== 'aucun') return false;
      } else {
        // Filtrer par badge spécifique
        if (product.badge !== filterBadge) return false;
      }
    }
    return true;
  });

  const resetFilters = () => {
    setSearchQuery('');
    setFilterPromo(false);
    setFilterCategory('all');
    setFilterStock('all');
    setFilterBadge('all');
    setShowOutOfStockOnly(false);
    setShowOrphanProducts(false);
  };

  const hasActiveFilters = searchQuery || filterPromo || filterCategory !== 'all' || filterStock !== 'all' || filterBadge !== 'all' || showOutOfStockOnly || showOrphanProducts;

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    try {
      // FIREBASE DIRECT
      await productsService.delete(id);
      loadProducts();
      toast.success('Produit supprimé', 'Le produit a été supprimé');
    } catch (error) {
      toast.error('Erreur', 'Impossible de supprimer le produit');
    }
  };

  const handleDuplicateProduct = async (product) => {
    if (!window.confirm(`Dupliquer "${product.name}" ?`)) return;
    try {
      const duplicatedProduct = {
        ...product,
        name: `${product.name} (copie)`,
      };
      delete duplicatedProduct.id;
      // FIREBASE DIRECT
      await productsService.create(duplicatedProduct);
      loadProducts();
      toast.success('Produit dupliqué', `"${product.name}" a été copié`);
    } catch (error) {
      toast.error('Erreur', 'Impossible de dupliquer le produit');
    }
  };

  const handleStockStatus = async (productId, status) => {
    try {
      // Convertir le status en is_out_of_stock boolean
      const is_out_of_stock = status !== 'available';
      // FIREBASE DIRECT
      await productsService.toggleStock(productId, is_out_of_stock);
      loadProducts();

      const messages = {
        '2h': { title: 'Rupture 2h', msg: 'Produit en rupture pour 2 heures' },
        'today': { title: 'Rupture 24h', msg: 'Produit en rupture jusqu\'à minuit' },
        'indefinite': { title: 'Rupture stock', msg: 'Produit en rupture indéfinie' },
        'available': { title: 'Remis en stock', msg: 'Produit disponible' }
      };
      const m = messages[status];
      status === 'available' ? toast.success(m.title, m.msg) : toast.warning(m.title, m.msg);
    } catch (error) {
      toast.error('Erreur', 'Impossible de mettre à jour le stock');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      // FIREBASE DIRECT
      await categoriesService.delete(id);
      loadCategories();
      toast.success('Catégorie supprimée', 'La catégorie a été supprimée');
    } catch (error) {
      toast.error('Erreur', 'Impossible de supprimer la catégorie');
    }
  };

  // Upload image pour une catégorie
  const handleCategoryImageUpload = async (categoryId, file) => {
    try {
      // FIREBASE DIRECT: Upload vers Firebase Storage
      const result = await uploadService.categoryImage(file);
      if (result.url) {
        // Mettre à jour la catégorie avec la nouvelle image
        await categoriesService.update(categoryId, { image_url: result.url });
        loadCategories();
        toast.success('Image mise à jour', 'L\'image de la catégorie a été modifiée');
      }
    } catch (error) {
      console.error('Erreur upload image:', error);
      toast.error('Erreur upload', 'Impossible d\'uploader l\'image');
    }
  };

  // Upload image pour un choix de la bibliothèque
  const handleChoiceImageUpload = async (choiceId, file) => {
    try {
      // FIREBASE DIRECT: Upload vers Firebase Storage
      const result = await uploadService.choiceImage(file);
      if (result.url) {
        // Mettre à jour le choix avec la nouvelle image
        await choiceLibraryService.update(choiceId, { image_url: result.url });
        loadChoiceLibrary();
        toast.success('Image mise à jour', 'L\'image du choix a été modifiée');
      }
    } catch (error) {
      console.error('Erreur upload image:', error);
      toast.error('Erreur upload', 'Impossible d\'uploader l\'image');
    }
  };

  const handleDuplicateCategory = async (category) => {
    if (!window.confirm(`Dupliquer la catégorie "${category.name}" ?`)) return;
    try {
      const duplicatedCategory = {
        ...category,
        name: `${category.name} (copie)`,
      };
      delete duplicatedCategory.id;
      delete duplicatedCategory.order; // Reset order so it goes to the end
      // FIREBASE DIRECT
      await categoriesService.create(duplicatedCategory);
      loadCategories();
      toast.success('Catégorie dupliquée', `"${category.name}" a été copiée`);
    } catch (error) {
      toast.error('Erreur', 'Impossible de dupliquer la catégorie');
      console.error(error);
    }
  };

  const handleReorderCategory = async (categoryId, direction) => {
    const sortedCats = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    const index = sortedCats.findIndex(c => c.id === categoryId);
    if (index === -1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sortedCats.length) return;

    try {
      const category1 = sortedCats[index];
      const category2 = sortedCats[targetIndex];

      // FIREBASE DIRECT
      await categoriesService.update(category1.id, {
        ...category1,
        order: category2.order || targetIndex
      });

      await categoriesService.update(category2.id, {
        ...category2,
        order: category1.order || index
      });

      await loadCategories();
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Erreur', 'Impossible de réordonner');
    }
  };

  const handleMoveCategoryUp = async (category, index) => {
    if (index === 0) return; // Already at top

    const sortedCategories = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    const prevCategory = sortedCategories[index - 1];

    try {
      // FIREBASE DIRECT - Swap orders
      await categoriesService.update(category.id, { order: prevCategory.order || 0 });
      await categoriesService.update(prevCategory.id, { order: category.order || 0 });
      loadCategories();
    } catch (error) {
      toast.error('Erreur', 'Impossible de déplacer');
    }
  };

  const handleMoveCategoryDown = async (category, index) => {
    const sortedCategories = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    if (index === sortedCategories.length - 1) return; // Already at bottom

    const nextCategory = sortedCategories[index + 1];

    try {
      // FIREBASE DIRECT - Swap orders
      await categoriesService.update(category.id, { order: nextCategory.order || 0 });
      await categoriesService.update(nextCategory.id, { order: category.order || 0 });
      loadCategories();
    } catch (error) {
      toast.error('Erreur', 'Impossible de déplacer');
    }
  };

  // Drag & Drop handlers for categories
  const handleCategoryDragStart = (e, index) => {
    setDraggedCategoryIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCategoryDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCategoryDrop = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedCategoryIndex === null || draggedCategoryIndex === dropIndex) {
      setDraggedCategoryIndex(null);
      return;
    }

    const sortedCategories = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    const draggedCategory = sortedCategories[draggedCategoryIndex];
    const targetCategory = sortedCategories[dropIndex];

    try {
      // FIREBASE DIRECT - Swap orders
      await categoriesService.update(draggedCategory.id, { order: targetCategory.order || dropIndex });
      await categoriesService.update(targetCategory.id, { order: draggedCategory.order || draggedCategoryIndex });
      await loadCategories();
      setDraggedCategoryIndex(null);
    } catch (error) {
      toast.error('Erreur', 'Impossible de déplacer');
      setDraggedCategoryIndex(null);
    }
  };

  // Drag & Drop handlers for products
  const handleProductDragStart = (e, index) => {
    setDraggedProductIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleProductDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleProductDrop = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedProductIndex === null || draggedProductIndex === dropIndex) {
      setDraggedProductIndex(null);
      return;
    }

    // FIX: Uniformiser sur display_order au lieu de order
    const sortedProducts = [...filteredProducts].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const draggedProduct = sortedProducts[draggedProductIndex];
    const targetProduct = sortedProducts[dropIndex];

    try {
      // FIREBASE DIRECT - Swap display_order
      await productsService.update(draggedProduct.id, { display_order: targetProduct.display_order || dropIndex });
      await productsService.update(targetProduct.id, { display_order: draggedProduct.display_order || draggedProductIndex });
      await loadProducts();
      setDraggedProductIndex(null);
    } catch (error) {
      toast.error('Erreur', 'Impossible de déplacer');
      setDraggedProductIndex(null);
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Supprimer cette option ?')) return;
    try {
      // FIREBASE DIRECT
      await optionsService.delete(optionId);
      loadOptions();
      toast.success('Option supprimée', 'L\'option a été supprimée');
    } catch (error) {
      toast.error('Erreur', 'Impossible de supprimer l\'option');
    }
  };

  const handleDuplicateOption = async (option) => {
    if (!window.confirm(`Dupliquer l'option "${option.name}" ?`)) return;
    try {
      const duplicatedOption = {
        ...option,
        name: `${option.name} (copie)`,
      };
      delete duplicatedOption.id;
      // FIREBASE DIRECT
      await optionsService.create(duplicatedOption);
      loadOptions();
      toast.success('Option dupliquée', `"${option.name}" a été copiée`);
    } catch (error) {
      toast.error('Erreur', 'Impossible de dupliquer l\'option');
    }
  };

  const handleDeleteChoice = async (choiceId) => {
    if (!window.confirm('Supprimer ce choix de la bibliothèque ?')) return;
    try {
      // FIREBASE DIRECT
      await choiceLibraryService.delete(choiceId);
      loadChoiceLibrary();
      toast.success('Choix supprimé', 'Le choix a été supprimé de la bibliothèque');
    } catch (error) {
      toast.error('Erreur', 'Impossible de supprimer le choix');
    }
  };

  // === TRI DES PRODUITS PAR CATÉGORIE (Drag & Drop) ===
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const openSortModal = (category) => {
    // Récupérer les produits de cette catégorie triés par display_order
    const categoryProducts = products
      .filter(p => p.category === category.name)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    setSortingCategory(category);
    setSortedProducts(categoryProducts);
    setShowSortModal(true);
  };

  const closeSortModal = () => {
    setShowSortModal(false);
    setSortingCategory(null);
    setSortedProducts([]);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setSortedProducts((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const saveSortOrder = async () => {
    setSavingSort(true);
    try {
      const sortData = sortedProducts.map((product, index) => ({
        id: product.id,
        display_order: index
      }));
      // FIREBASE DIRECT
      await productsService.updateSortOrder(sortData);
      await loadProducts();
      toast.success('Ordre sauvegardé', `${sortedProducts.length} produit(s) réordonnés`);
      closeSortModal();
    } catch (error) {
      console.error('Erreur sauvegarde ordre:', error);
      toast.error('Erreur', 'Impossible de sauvegarder l\'ordre');
    } finally {
      setSavingSort(false);
    }
  };

  // Diagnostic: Rechercher un choix spécifique (incluant sub_options)
  const handleDiagnosticSearch = () => {
    const searchTerm = window.prompt('Entrez le nom du choix à rechercher (ex: COCA):');
    if (!searchTerm) return;

    const searchLower = searchTerm.toLowerCase();
    const results = [];

    // Fonction récursive pour chercher dans les sub_options
    const searchInChoices = (choices, optionName, path = '') => {
      if (!choices || !Array.isArray(choices)) return;

      choices.forEach(choice => {
        const currentPath = path ? `${path} → ${choice.name || '?'}` : (choice.name || '?');

        if (choice.name && choice.name.toLowerCase().includes(searchLower)) {
          results.push({
            source: 'Option',
            optionName: optionName,
            choiceName: choice.name,
            path: path || '(niveau principal)',
            choiceData: choice
          });
        }

        // Chercher dans les sub_options
        if (choice.sub_options && Array.isArray(choice.sub_options)) {
          choice.sub_options.forEach(subOption => {
            if (subOption.choices) {
              searchInChoices(subOption.choices, optionName, `${choice.name} → ${subOption.name || 'sous-option'}`);
            }
          });
        }
      });
    };

    // Chercher dans les options
    options.forEach(option => {
      searchInChoices(option.choices, option.name);
    });

    // Chercher dans la bibliothèque
    choiceLibrary.forEach(choice => {
      if (choice.name && choice.name.toLowerCase().includes(searchLower)) {
        results.push({
          source: 'Bibliothèque',
          choiceName: choice.name,
          choiceId: choice.id,
          choiceData: choice
        });
      }
    });

    console.log(`[DIAGNOSTIC] Recherche "${searchTerm}":`, results);

    if (results.length === 0) {
      toast.error('Aucun résultat', `Aucun choix contenant "${searchTerm}" trouvé dans les options ou la bibliothèque`);
    } else {
      const optionResults = results.filter(r => r.source === 'Option');
      const libraryResults = results.filter(r => r.source === 'Bibliothèque');

      let message = `Trouvé ${results.length} résultat(s):\n\n`;

      if (optionResults.length > 0) {
        message += `📋 DANS LES OPTIONS (${optionResults.length}):\n`;
        optionResults.forEach(r => {
          message += `  - "${r.choiceName}"\n`;
          message += `    Option: "${r.optionName}"\n`;
          if (r.path && r.path !== '(niveau principal)') {
            message += `    Chemin: ${r.path}\n`;
          }
        });
      }

      if (libraryResults.length > 0) {
        message += `\n📚 DANS LA BIBLIOTHÈQUE (${libraryResults.length}):\n`;
        libraryResults.forEach(r => {
          message += `  - "${r.choiceName}" (ID: ${r.choiceId})\n`;
        });
      }

      if (optionResults.length > 0 && libraryResults.length === 0) {
        message += `\n⚠️ PROBLÈME: Le choix existe dans les options mais PAS dans la bibliothèque!\n`;
        message += `Cliquez sur "Sync biblio" pour l'ajouter.`;
      }

      alert(message);
    }
  };

  // Synchroniser tous les choix vers la bibliothèque
  const handleSyncLibrary = async () => {
    // Compter d'abord combien de choix on a (incluant sub_options)
    let totalChoices = 0;
    let optionsWithChoices = 0;

    const countChoices = (choices) => {
      if (!choices || !Array.isArray(choices)) return 0;
      let count = 0;
      for (const choice of choices) {
        if (choice.name) count++;
        // Compter aussi les choix dans les sub_options
        if (choice.sub_options && Array.isArray(choice.sub_options)) {
          for (const subOption of choice.sub_options) {
            if (subOption.choices) {
              count += countChoices(subOption.choices);
            }
          }
        }
      }
      return count;
    };

    for (const option of options) {
      const choiceCount = countChoices(option.choices);
      if (choiceCount > 0) {
        optionsWithChoices++;
        totalChoices += choiceCount;
      }
    }

    if (totalChoices === 0) {
      toast.error('Aucun choix trouvé', `${options.length} options analysées, aucun choix trouvé (même dans les sous-options).`);
      return;
    }

    setSyncing(true);
    try {
      // 1. Charger toute la bibliothèque en une seule requête
      const { choices: existingChoices } = await choiceLibraryService.getAll();
      const libraryMap = new Map(existingChoices.map(c => [c.name?.toLowerCase(), c]));
      console.log(`[SYNC] Bibliothèque existante: ${existingChoices.length} choix`);

      // 2. Collecter tous les choix uniques des options (y compris sub_options imbriquées)
      const choicesMap = new Map();

      // Fonction récursive pour extraire les choix des sub_options
      const extractChoices = (choices, depth = 0) => {
        if (!choices || !Array.isArray(choices)) return;

        for (const choice of choices) {
          // Ajouter ce choix s'il a un nom
          if (choice.name) {
            const key = choice.name.toLowerCase();
            if (!choicesMap.has(key) || choice.image_url) {
              choicesMap.set(key, {
                name: choice.name,
                default_price: choice.price || 0,
                image_url: choice.image_url || ''
              });
            }
          }

          // Parcourir les sub_options de ce choix
          if (choice.sub_options && Array.isArray(choice.sub_options)) {
            for (const subOption of choice.sub_options) {
              // Extraire les choix de chaque sous-option
              if (subOption.choices) {
                extractChoices(subOption.choices, depth + 1);
              }
            }
          }
        }
      };

      // Parcourir toutes les options
      for (const option of options) {
        extractChoices(option.choices);
      }

      console.log(`[SYNC] Choix extraits (incluant sub_options): ${choicesMap.size}`);

      // Calculer combien seront ajoutés vs mis à jour
      let willAdd = 0, willUpdate = 0;
      const newChoices = [];
      for (const [key, choice] of choicesMap) {
        if (libraryMap.has(key)) {
          willUpdate++;
        } else {
          willAdd++;
          newChoices.push(choice.name);
        }
      }

      console.log(`[SYNC] Choix uniques: ${choicesMap.size} (${willAdd} nouveaux, ${willUpdate} existants)`);
      console.log(`[SYNC] Nouveaux choix à ajouter:`, newChoices);

      if (willAdd === 0 && !window.confirm(`Tous les ${choicesMap.size} choix existent déjà dans la bibliothèque. Mettre à jour ${willUpdate} choix existants ?`)) {
        setSyncing(false);
        return;
      }

      if (willAdd > 0 && !window.confirm(`Ajouter ${willAdd} nouveaux choix et mettre à jour ${willUpdate} existants ?\n\nNouveaux: ${newChoices.slice(0, 10).join(', ')}${newChoices.length > 10 ? '...' : ''}`)) {
        setSyncing(false);
        return;
      }

      // 3. Créer/Mettre à jour en parallèle par lots de 10
      let added = 0, updated = 0, skipped = 0;
      const entries = Array.from(choicesMap.entries());
      const batchSize = 10;

      for (let i = 0; i < entries.length; i += batchSize) {
        const batch = entries.slice(i, i + batchSize);
        await Promise.all(batch.map(async ([key, choice]) => {
          try {
            const existing = libraryMap.get(key);
            if (existing) {
              await choiceLibraryService.update(existing.id, {
                default_price: choice.default_price,
                image_url: choice.image_url
              });
              updated++;
            } else {
              await choiceLibraryService.create(choice);
              added++;
            }
          } catch (err) {
            console.error(`[SYNC] Erreur pour "${choice.name}":`, err);
            skipped++;
          }
        }));
      }

      await loadChoiceLibrary();
      const message = `${added} ajouté(s), ${updated} mis à jour${skipped > 0 ? `, ${skipped} erreur(s)` : ''}`;
      toast.success('Synchronisation terminée', message);
      console.log(`[SYNC] Terminé:`, message);
    } catch (error) {
      console.error('Erreur sync bibliothèque:', error);
      toast.error('Erreur', error.message);
    } finally {
      setSyncing(false);
    }
  };

  // FIX Build 214: Sync Bibliothèque → Options (met à jour les images des options depuis la bibliothèque)
  const handleSyncFromLibrary = async () => {
    if (!window.confirm('Mettre à jour les images des OPTIONS depuis la BIBLIOTHÈQUE ?\n\nCela va copier les images de la bibliothèque vers toutes les options.')) return;

    setSyncingFromLibrary(true);
    try {
      const result = await syncAllLibraryToOptions();
      console.log('[SYNC FROM LIBRARY] Résultat:', result);

      if (result.totalUpdated > 0) {
        toast.success('Images mises à jour!', `${result.totalUpdated} images synchronisées dans ${result.optionsUpdated} options`);
        await loadOptions(); // Recharger les options
      } else {
        toast.success('Déjà à jour', 'Toutes les images sont déjà synchronisées');
      }
    } catch (error) {
      console.error('Erreur sync from library:', error);
      toast.error('Erreur', error.message);
    } finally {
      setSyncingFromLibrary(false);
    }
  };

  const tabs = [
    { id: 'products', label: '🍔 Produits', icon: Package, count: products.length },
    { id: 'categories', label: '📁 Catégories', icon: FolderOpen, count: categories.length },
    { id: 'options', label: '🎛️ Options', icon: Sliders, count: options.length },
    { id: 'choice-library', label: '📚 Bibliothèque', icon: Package, count: choiceLibrary.length }
  ];

  if (loading) {
    return (
      <div>
        <Header title="📋 Gestion du Menu" subtitle="Gérez vos produits, catégories et options" />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="📋 Gestion du Menu" subtitle="Gérez vos produits, catégories et options" />
      
      {/* Onglets horizontaux */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex space-x-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-3 px-6 py-3 rounded-lg font-bold transition-all ${
                    activeTab === tab.id 
                      ? 'bg-primary text-white shadow-lg scale-105' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contenu selon l'onglet actif */}
      <div className="p-6">
        {/* PRODUITS */}
        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-2xl font-bold">🍔 Produits ({filteredProducts.length})</h2>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                >
                  {viewMode === 'grid' ? <ListIcon className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
                </Button>
                <Button 
                  onClick={() => {
                    setEditingProduct(null);
                    setShowProductModal(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau produit
                </Button>
              </div>
            </div>
            
            {/* Filtres */}
            <div className="mb-4 p-4 bg-white rounded-lg shadow">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 border rounded-lg text-sm"
                  />
                </div>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="all">Toutes catégories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                <select
                  value={filterStock}
                  onChange={(e) => setFilterStock(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="all">Tous stocks</option>
                  <option value="available">Disponibles</option>
                  <option value="out_of_stock">En rupture</option>
                </select>
                <select
                  value={filterBadge}
                  onChange={(e) => setFilterBadge(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="all">Tous badges</option>
                  <option value="none">Sans badge</option>
                  <option value="bestseller">⭐ Best Seller</option>
                  <option value="nouveau">✨ Nouveau</option>
                  <option value="promo">🔥 Promo</option>
                </select>
                <div className="flex gap-2">
                  <Button
                    variant={filterPromo ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterPromo(!filterPromo)}
                  >
                    Promo
                  </Button>
                  <Button
                    variant={showOrphanProducts ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowOrphanProducts(!showOrphanProducts)}
                    className={showOrphanProducts ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}
                  >
                    🚫 Sans catégorie
                  </Button>
                  {hasActiveFilters && (
                    <Button variant="outline" size="sm" onClick={resetFilters}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {viewMode === 'list' ? (
              <ProductsListView 
                products={filteredProducts}
                categories={categories}
                onEdit={(p) => { setEditingProduct(p); setShowProductModal(true); }}
                onDelete={handleDeleteProduct}
                onDuplicate={handleDuplicateProduct}
                onStockClick={(id) => setOpenStockMenu(openStockMenu === id ? null : id)}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map((product, productIndex) => {
                const price = product.base_price || product.basePrice || 0;
                let imageUrl = product.image_url || product.image;
                // Construire l'URL complète si nécessaire
                if (imageUrl) {
                  // Si c'est une ancienne URL d'un autre domaine, extraire le chemin
                  if (imageUrl.includes('.preview.emergentagent.com/uploads/')) {
                    const match = imageUrl.match(/\/uploads\/.+$/);
                    if (match) {
                      imageUrl = `${API_URL}${match[0]}`;
                    }
                  } else if (imageUrl.startsWith('http')) {
                    // URL externe complète (Unsplash, etc.) - garder telle quelle
                    imageUrl = imageUrl;
                  } else if (!imageUrl.startsWith('http')) {
                    if (imageUrl.startsWith('/')) {
                      imageUrl = `${API_URL}${imageUrl}`;
                    } else {
                      imageUrl = `${API_URL}/${imageUrl}`;
                    }
                  }
                }
                return (
                  <Card 
                    key={product.id}
                    draggable
                    onDragStart={(e) => handleProductDragStart(e, productIndex)}
                    onDragOver={handleProductDragOver}
                    onDrop={(e) => handleProductDrop(e, productIndex)}
                    className={`cursor-move transition-all ${draggedProductIndex === productIndex ? 'opacity-50 scale-95' : ''}`}
                  >
                    {/* Zone cliquable pour ouvrir le modal */}
                    <div
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        setEditingProduct(product);
                        setShowProductModal(true);
                      }}
                    >
                      {/* Image avec badge produit */}
                      <div className="relative">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={product.name}
                            className="w-full h-48 object-cover rounded-lg mb-4"
                          />
                        ) : (
                          <div className="w-full h-48 bg-gray-100 rounded-lg mb-4 flex items-center justify-center">
                            <Package className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                        {/* Badge produit (bestseller, nouveau, promo) */}
                        {product.badge && product.badge !== 'aucun' && (
                          <span
                            className="absolute top-2 left-2 px-3 py-1 rounded-lg text-xs font-bold text-white shadow-lg"
                            style={{
                              backgroundColor:
                                product.badge === 'bestseller' ? '#F59E0B' :
                                product.badge === 'nouveau' ? '#3B82F6' :
                                product.badge === 'promo' ? '#EF4444' : '#6B7280'
                            }}
                          >
                            {product.badge === 'bestseller' ? '⭐ BEST-SELLER' :
                             product.badge === 'nouveau' ? '✨ NOUVEAU' :
                             product.badge === 'promo' ? '🔥 PROMO' :
                             product.badge.toUpperCase()}
                          </span>
                        )}
                        {/* Badge PROMO automatique si promotion active (enrichi par backend) */}
                        {product.has_promo && (!product.badge || product.badge === 'aucun') && (
                          <span
                            className="absolute top-2 left-2 px-3 py-1 rounded-lg text-xs font-bold text-white shadow-lg"
                            style={{ backgroundColor: '#EF4444' }}
                          >
                            🏷️ PROMO
                          </span>
                        )}
                        {/* Badge RUPTURE superposé si en rupture */}
                        {product.is_out_of_stock && (
                          <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center">
                            <span className="px-4 py-2 bg-red-600 text-white font-bold rounded-lg text-sm">
                              {product.stock_resume_at ? '⏰ RUPTURE MOMENTANÉE' : '🚫 RUPTURE'}
                            </span>
                          </div>
                        )}
                      </div>
                      <h4 className="font-bold text-lg mb-2">{product.name}</h4>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                    </div>
                    
                    {/* Stock Status Badge */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-black text-primary">{price.toFixed(2)}€</span>
                      {product.is_out_of_stock ? (
                        <div className="flex flex-col items-end">
                          {(() => {
                            const now = new Date();
                            const resumeAt = product.stock_resume_at ? new Date(product.stock_resume_at) : null;
                            
                            if (!resumeAt) {
                              // Rupture indéfinie
                              return (
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-700 text-white">
                                  ⛔ Rupture indéfinie
                                </span>
                              );
                            }
                            
                            const diffHours = (resumeAt - now) / (1000 * 60 * 60);
                            const isToday = resumeAt.toDateString() === now.toDateString();
                            const isTomorrow = resumeAt.toDateString() === new Date(now.getTime() + 24*60*60*1000).toDateString();
                            
                            if (diffHours <= 2.5 && diffHours > 0) {
                              // Rupture 2H
                              return (
                                <>
                                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700">
                                    ⏰ Rupture 2H
                                  </span>
                                  <span className="text-xs text-gray-500 mt-1">
                                    Retour {resumeAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </>
                              );
                            } else if (isToday || (isTomorrow && resumeAt.getHours() === 0)) {
                              // Rupture aujourd'hui (jusqu'à minuit)
                              return (
                                <>
                                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                                    📅 Rupture journée
                                  </span>
                                  <span className="text-xs text-gray-500 mt-1">
                                    Retour demain
                                  </span>
                                </>
                              );
                            } else {
                              // Autre (date future)
                              return (
                                <>
                                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                                    🚫 Rupture
                                  </span>
                                  <span className="text-xs text-gray-500 mt-1">
                                    {resumeAt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} {resumeAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </>
                              );
                            }
                          })()}
                        </div>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                          ✅ En stock
                        </span>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="space-y-2 mb-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setEditingVisualProduct(product);
                          setShowVisualModal(true);
                        }}
                        className="w-full bg-blue-50 hover:bg-blue-100 border-blue-200"
                        title="Image & Badge"
                      >
                        <Image className="w-4 h-4 mr-2" />
                        Image & Badge
                      </Button>
                      <div className="grid grid-cols-2 gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => {
                            setEditingProduct(product);
                            setShowProductModal(true);
                          }}
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleDuplicateProduct(product)}
                          title="Dupliquer"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Stock Management Dropdown */}
                    <div className="relative mb-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setOpenStockMenu(openStockMenu === product.id ? null : product.id)}
                        className="w-full"
                        title="Gérer le stock"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Gérer le stock
                      </Button>
                      
                      {openStockMenu === product.id && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                          <button
                            onClick={() => {
                              handleStockStatus(product.id, '2h');
                              setOpenStockMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
                          >
                            <Clock className="w-4 h-4 mr-2 text-orange-500" />
                            Rupture 2 heures
                          </button>
                          <button
                            onClick={() => {
                              handleStockStatus(product.id, 'today');
                              setOpenStockMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
                          >
                            <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                            Rupture aujourd'hui
                          </button>
                          <button
                            onClick={() => {
                              handleStockStatus(product.id, 'indefinite');
                              setOpenStockMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center border-b"
                          >
                            <XCircle className="w-4 h-4 mr-2 text-red-500" />
                            Rupture indéfinie
                          </button>
                          <button
                            onClick={() => {
                              handleStockStatus(product.id, 'available');
                              setOpenStockMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center text-green-600 font-bold"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Remettre en stock
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Delete Button */}
                    <Button 
                      size="sm" 
                      variant="danger" 
                      onClick={() => handleDeleteProduct(product.id)}
                      title="Supprimer"
                      className="w-full"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer
                    </Button>
                  </Card>
                );
              })}
              </div>
            )}
          </div>
        )}

        {/* CATÉGORIES */}
        {activeTab === 'categories' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">📁 Catégories ({categories.length})</h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                >
                  {viewMode === 'grid' ? <ListIcon className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={() => {
                    setEditingCategory(null);
                    setShowCategoryModal(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle catégorie
                </Button>
              </div>
            </div>

            {/* Barre de recherche Catégories */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une catégorie..."
                  value={categoriesSearchTerm}
                  onChange={(e) => setCategoriesSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                />
                {categoriesSearchTerm && (
                  <button
                    onClick={() => setCategoriesSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {(() => {
              const filteredCategories = categories.filter(cat =>
                cat.name.toLowerCase().includes(categoriesSearchTerm.toLowerCase()) ||
                (cat.description && cat.description.toLowerCase().includes(categoriesSearchTerm.toLowerCase()))
              );

              return viewMode === 'list' ? (
              <CategoriesListView
                categories={[...filteredCategories].sort((a, b) => (a.order || 0) - (b.order || 0))}
                products={products}
                onEdit={(c) => { setEditingCategory(c); setShowCategoryModal(true); }}
                onDelete={handleDeleteCategory}
                onReorder={handleReorderCategory}
                onDuplicate={handleDuplicateCategory}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...filteredCategories].sort((a, b) => (a.order || 0) - (b.order || 0)).map((category, index) => {
                let imageUrl = category.image_url || category.image;
                // Construire l'URL complète si nécessaire
                if (imageUrl) {
                  // Si c'est une ancienne URL d'un autre domaine, extraire le chemin
                  if (imageUrl.includes('.preview.emergentagent.com/uploads/')) {
                    const match = imageUrl.match(/\/uploads\/.+$/);
                    if (match) {
                      imageUrl = `${API_URL}${match[0]}`;
                    }
                  } else if (imageUrl.startsWith('http')) {
                    // URL externe complète (Unsplash, etc.) - garder telle quelle
                    imageUrl = imageUrl;
                  } else if (!imageUrl.startsWith('http')) {
                    if (imageUrl.startsWith('/')) {
                      imageUrl = `${API_URL}${imageUrl}`;
                    } else {
                      imageUrl = `${API_URL}/${imageUrl}`;
                    }
                  }
                }
                const sortedCategories = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
                const isFirst = index === 0;
                const isLast = index === sortedCategories.length - 1;
                
                return (
                  <Card 
                    key={category.id}
                    draggable
                    onDragStart={(e) => handleCategoryDragStart(e, index)}
                    onDragOver={handleCategoryDragOver}
                    onDrop={(e) => handleCategoryDrop(e, index)}
                    className={`cursor-move transition-all ${draggedCategoryIndex === index ? 'opacity-50 scale-95' : ''}`}
                  >
                    {/* Zone cliquable pour ouvrir le modal de tri */}
                    <div
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => openSortModal(category)}
                    >
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={category.name}
                          className="w-full h-40 object-cover rounded-lg mb-4"
                        />
                      ) : (
                        <div className="w-full h-40 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg mb-4 flex items-center justify-center">
                          <FolderOpen className="w-12 h-12 text-orange-400" />
                        </div>
                      )}
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-bold text-lg">{category.name}</h4>
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">#{index + 1}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{category.description}</p>
                    </div>
                    
                    {/* Compteur produits + indication tri */}
                    <div
                      className="mb-3 p-3 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors"
                      onClick={() => openSortModal(category)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium text-purple-700">
                            {products.filter(p => p.category === category.name).length} produit(s)
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-purple-600">
                          <GripVertical className="w-4 h-4" />
                          <span className="text-xs font-medium">Trier</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Bouton Changer image */}
                    <div className="mb-2">
                      <label className="w-full cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleCategoryImageUpload(category.id, file);
                            }
                          }}
                        />
                        <div className="flex items-center justify-center gap-2 px-3 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm font-medium">
                          <Image className="w-4 h-4" />
                          📷 Changer l'image
                        </div>
                      </label>
                    </div>

                    {/* Edit and Delete buttons */}
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setEditingCategory(category);
                          setShowCategoryModal(true);
                        }}
                        className="flex-1"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="danger" 
                        onClick={() => handleDeleteCategory(category.id)}
                        className="flex-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                </Card>
              );
              })}
              </div>
            );
            })()}
          </div>
        )}

        {/* OPTIONS */}
        {activeTab === 'options' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">🎛️ Options ({options.length})</h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSyncFromLibrary}
                  disabled={syncingFromLibrary}
                  title="Mettre à jour les images des options depuis la bibliothèque"
                  className="bg-green-50 hover:bg-green-100 border-green-300"
                >
                  <Download className={`w-4 h-4 mr-2 ${syncingFromLibrary ? 'animate-bounce' : ''}`} />
                  {syncingFromLibrary ? 'Sync...' : '📚→ Sync images'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDiagnosticSearch}
                  title="Rechercher un choix dans les options et la bibliothèque"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Diagnostic
                </Button>
                <Button
                  variant={showUnusedOptions ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowUnusedOptions(!showUnusedOptions)}
                  className={showUnusedOptions ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}
                  title="Afficher les options non utilisées dans aucun produit"
                >
                  🚫 Non utilisées ({options.filter(opt => !isOptionUsed(opt.id)).length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOptionsViewMode(optionsViewMode === 'grid' ? 'list' : 'grid')}
                >
                  {optionsViewMode === 'grid' ? <ListIcon className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={() => {
                    setEditingOption(null);
                    setShowOptionModal(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle option
                </Button>
              </div>
            </div>

            {/* Barre de recherche Options */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une option..."
                  value={optionsSearchTerm}
                  onChange={(e) => setOptionsSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                />
                {optionsSearchTerm && (
                  <button
                    onClick={() => setOptionsSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {(() => {
              let filteredOptions = options.filter(opt =>
                opt.name.toLowerCase().includes(optionsSearchTerm.toLowerCase()) ||
                (opt.description && opt.description.toLowerCase().includes(optionsSearchTerm.toLowerCase())) ||
                (opt.internal_comment && opt.internal_comment.toLowerCase().includes(optionsSearchTerm.toLowerCase()))
              );

              // Filtre options non utilisées
              if (showUnusedOptions) {
                filteredOptions = filteredOptions.filter(opt => !isOptionUsed(opt.id));
              }

              return optionsViewMode === 'list' ? (
              <OptionsListView
                options={filteredOptions}
                onEdit={(opt) => { setEditingOption(opt); setShowOptionModal(true); }}
                onDelete={handleDeleteOption}
                onDuplicate={handleDuplicateOption}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredOptions.map((option) => (
                <Card key={option.id}>
                  <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex-1">
                        <span className="text-lg font-bold">{option.name}</span>
                        {option.internal_comment && (
                          <p className="text-xs text-yellow-700 mt-1 font-normal">💬 {option.internal_comment}</p>
                        )}
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => {
                            setEditingOption(option);
                            setShowOptionModal(true);
                          }}
                          className="p-2 hover:bg-white rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleDuplicateOption(option)}
                          className="p-2 hover:bg-white rounded-lg transition-colors"
                          title="Dupliquer"
                        >
                          <Copy className="w-4 h-4 text-green-600" />
                        </button>
                        <button
                          onClick={() => handleDeleteOption(option.id)}
                          className="p-2 hover:bg-white rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </CardTitle>
                  </CardHeader>

                  <CardContent 
                    className="space-y-3 cursor-pointer hover:bg-gray-50 transition-colors rounded-lg"
                    onClick={() => {
                      setEditingOption(option);
                      setShowOptionModal(true);
                    }}
                  >
                    {option.description && (
                      <p className="text-sm text-gray-600">{option.description}</p>
                    )}
                    
                    <div className="flex items-center space-x-2 text-sm">
                      <span className={`px-3 py-1 rounded-full font-bold ${
                        option.is_required 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {option.is_required ? 'Obligatoire' : 'Optionnel'}
                      </span>
                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-bold">
                        {option.type === 'single' ? 'Choix unique' : 'Choix multiple'}
                      </span>
                    </div>

                    {option.type === 'multiple' && option.max_choices && (
                      <p className="text-xs text-gray-500">
                        Maximum {option.max_choices} choix
                      </p>
                    )}

                    <div className="pt-3 border-t">
                      <p className="text-xs font-semibold text-gray-600 mb-2">
                        Choix disponibles ({option.choices?.length || 0})
                      </p>
                      <div className="space-y-1">
                        {option.choices?.slice(0, 3).map((choice, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-700">{choice.name}</span>
                            {(choice.price || choice.price_modifier || 0) > 0 && (
                              <span className="text-green-600 font-semibold">
                                +{(choice.price || choice.price_modifier || 0).toFixed(2)}€
                              </span>
                            )}
                          </div>
                        ))}
                        {option.choices?.length > 3 && (
                          <p className="text-xs text-gray-400 italic">
                            +{option.choices.length - 3} autres choix...
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            );
            })()}
          </div>
        )}

        {/* BIBLIOTHÈQUE DE CHOIX */}
        {activeTab === 'choice-library' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold">📚 Bibliothèque de Choix ({choiceLibrary.filter(c => 
                  c.name.toLowerCase().includes(librarySearchTerm.toLowerCase())
                ).length})</h2>
                <p className="text-sm text-gray-600 mt-1">Créez des choix réutilisables avec images pour vos options</p>
              </div>
              <Button 
                onClick={() => {
                  setEditingChoice(null);
                  setShowChoiceModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau choix
              </Button>
            </div>

            {/* Barre de recherche et boutons de vue */}
            <div className="flex gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un choix (nom, catégorie, prix...)"
                  value={librarySearchTerm}
                  onChange={(e) => setLibrarySearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                />
                {librarySearchTerm && (
                  <button
                    onClick={() => setLibrarySearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              {/* Filtre choix non utilisés */}
              <Button
                variant={showUnusedChoices ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowUnusedChoices(!showUnusedChoices)}
                className={showUnusedChoices ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}
              >
                🚫 Non utilisés ({choiceLibrary.filter(c => !isChoiceUsed(c.name)).length})
              </Button>

              {/* Boutons de vue */}
              <div className="flex gap-2">
                <Button
                  variant={libraryViewMode === 'grid' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLibraryViewMode('grid')}
                  title="Vue grille"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={libraryViewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLibraryViewMode('list')}
                  title="Vue liste"
                >
                  <ListIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>

{(() => {
              let filteredChoices = choiceLibrary.filter(c =>
                c.name.toLowerCase().includes(librarySearchTerm.toLowerCase())
              );
              // Filtre choix non utilisés
              if (showUnusedChoices) {
                filteredChoices = filteredChoices.filter(c => !isChoiceUsed(c.name));
              }

              if (choiceLibrary.length === 0) {
                return (
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 text-center">
                    <Package className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-blue-900 mb-2">Bibliothèque vide</h3>
                    <p className="text-sm text-blue-700 mb-4">
                      Créez vos premiers choix réutilisables (Chantilly, Ketchup, Bacon...)
                    </p>
                    <Button onClick={() => setShowChoiceModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Créer mon premier choix
                    </Button>
                  </div>
                );
              }

              if (filteredChoices.length === 0) {
                return (
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 text-center">
                    <Search className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-yellow-900 mb-2">Aucun résultat</h3>
                    <p className="text-sm text-yellow-700 mb-4">
                      Aucun choix ne correspond à votre recherche "{librarySearchTerm}"
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => setLibrarySearchTerm('')}
                    >
                      Effacer la recherche
                    </Button>
                  </div>
                );
              }

              if (libraryViewMode === 'list') {
                return (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Choix
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Description
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Prix
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredChoices.map((choice) => (
                            <tr key={choice.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  {choice.image_url ? (
                                    <img 
                                      src={choice.image_url} 
                                      alt={choice.name}
                                      className="w-12 h-12 object-cover rounded-lg mr-4"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  ) : (
                                    <div className="w-12 h-12 bg-gray-100 rounded-lg mr-4 flex items-center justify-center">
                                      <Package className="w-6 h-6 text-gray-400" />
                                    </div>
                                  )}
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{choice.name}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-600 max-w-xs truncate">
                                  {choice.description || '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm font-bold text-primary">
                                  {choice.default_price.toFixed(2)}€
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      setEditingChoice(choice);
                                      setShowChoiceModal(true);
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="danger"
                                    onClick={() => handleDeleteChoice(choice.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              }

              // Vue grille (par défaut)
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredChoices.map((choice) => (
                    <Card key={choice.id}>
                      <CardContent className="p-4">
                        {/* Image ou placeholder */}
                        <div className="relative mb-3">
                          {choice.image_url ? (
                            <img
                              src={choice.image_url}
                              alt={choice.name}
                              className="w-full h-32 object-cover rounded-lg"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                              <Package className="w-12 h-12 text-gray-300" />
                            </div>
                          )}
                          {/* Bouton changer image en overlay */}
                          <label className="absolute bottom-2 right-2 cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleChoiceImageUpload(choice.id, file);
                                }
                              }}
                            />
                            <div className="flex items-center gap-1 px-2 py-1 bg-white/90 hover:bg-white text-gray-700 rounded text-xs font-medium shadow">
                              <Image className="w-3 h-3" />
                              📷
                            </div>
                          </label>
                        </div>
                        <h3 className="font-bold text-lg mb-1">{choice.name}</h3>
                        {choice.description && (
                          <p className="text-sm text-gray-600 mb-2">{choice.description}</p>
                        )}
                        <p className="text-lg font-bold text-primary mb-3">
                          {choice.default_price.toFixed(2)}€
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingChoice(choice);
                              setShowChoiceModal(true);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Modifier
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDeleteChoice(choice.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Modals */}
      <ProductModal
        isOpen={showProductModal}
        onClose={() => {
          setShowProductModal(false);
          setEditingProduct(null);
        }}
        product={editingProduct}
        onSuccess={loadProducts}
      />

      <ProductVisualModal
        isOpen={showVisualModal}
        onClose={() => {
          setShowVisualModal(false);
          setEditingVisualProduct(null);
        }}
        product={editingVisualProduct}
        onSave={(updatedProduct) => {
          loadProducts();
          setShowVisualModal(false);
          setEditingVisualProduct(null);
        }}
      />

      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSuccess={loadCategories}
      />

      <OptionModal
        isOpen={showOptionModal}
        onClose={() => {
          setShowOptionModal(false);
          setEditingOption(null);
        }}
        option={editingOption}
        onSuccess={loadOptions}
      />

      <ChoiceLibraryModal
        isOpen={showChoiceModal}
        onClose={() => {
          setShowChoiceModal(false);
          setEditingChoice(null);
        }}
        choice={editingChoice}
        onSuccess={loadChoiceLibrary}
      />

      {/* Modal Tri des produits avec Drag & Drop */}
      {showSortModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col mx-4">
            {/* Header */}
            <div className="p-4 border-b bg-purple-50 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-purple-900">
                    ⇅ Trier les produits
                  </h3>
                  <p className="text-sm text-purple-700">
                    {sortingCategory?.name} • {sortedProducts.length} produit(s)
                  </p>
                  <p className="text-xs text-purple-500 mt-1">
                    Glissez-déposez pour réordonner ☰
                  </p>
                </div>
                <button
                  onClick={closeSortModal}
                  className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-purple-600" />
                </button>
              </div>
            </div>

            {/* Liste des produits avec Drag & Drop */}
            <div className="flex-1 overflow-y-auto p-4">
              {sortedProducts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  Aucun produit dans cette catégorie
                </p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortedProducts.map(p => p.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {sortedProducts.map((product, index) => (
                      <SortableProductItem
                        key={product.id}
                        product={product}
                        index={index}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={closeSortModal}
                disabled={savingSort}
              >
                Annuler
              </Button>
              <Button
                onClick={saveSortOrder}
                disabled={savingSort || sortedProducts.length === 0}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {savingSort ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Enregistrer l'ordre
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
